import { Button, Col, Container, Form, OverlayTrigger, Popover, Row, Table } from "react-bootstrap";
import StbButton from "../../../atoms/button/StbButton";
import { IProjectListProps } from "./models/IProjectListProps";
import "./ProjectList.css";
import { ArrowDownOutlined, ArrowUpOutlined, CloseOutlined, FilterOutlined, SearchOutlined, MoreOutlined } from "@ant-design/icons";
import { ChangeEvent, useEffect, useRef, useState } from "react";
import { Pagination, Dropdown } from "antd";
import { useMediaQuery } from 'react-responsive';
import { ProjectStatus } from "../../../../api";
import { isServerArchivedListItem } from "../../../../workPreparation/readiness";
import ProjectItemPage from "./projectitempage/ProjectItemPage";
import { usePostApiProjectsCopy, getApiProjectsGetProjectIdByReference } from "../../../../api/generated/endpoints/project/project";
import { archiveProject } from "../../../../api/workPreparationClient";
import { useLocation, useNavigate } from 'react-router-dom';
import type { ProjectListItemDto } from "../../../../api";
import { showErrorNotification, toastSuccess } from "../../../../utilities/messageUtils";

// Add new interface for grouped projects
interface GroupedProject {
  projectNumber: string;
  projects: ProjectListItemDto[];
}

const PROJECT_STATUS_FILTERS = [
  { status: ProjectStatus.WorkPreparationStarted, label: "Påbörjad", id: "filter-status-started" },
  { status: ProjectStatus.WorkPreparationFinished, label: "Klar", id: "filter-status-ready" },
  { status: ProjectStatus.Constructed, label: "Uppställd", id: "filter-status-constructed" },
  { status: ProjectStatus.Closed, label: "Avslutad", id: "filter-status-closed" },
  { status: ProjectStatus.Archived, label: "Arkiverad", id: "filter-status-archived" },
] as const;

const DEFAULT_PROJECT_STATUSES = [
  ProjectStatus.WorkPreparationStarted,
  ProjectStatus.WorkPreparationFinished,
  ProjectStatus.Constructed,
];

const ProjectList: React.FC<IProjectListProps> = (props) => {
  const [projects, setProjects] = useState<ProjectListItemDto[]>(props.listOfProjectDto || []);

  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  const toggleGroup = (projectNumber: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(projectNumber)) {
        next.delete(projectNumber);
      } else {
        next.add(projectNumber);
      }
      return next;
    });
  };

  const location = useLocation();
  const navigate = useNavigate();

  // Mutation for copying project
  const copyProjectMutation = usePostApiProjectsCopy({
    mutation: {
      onSuccess: () => {
        props.refetchProjects();
      },
    },
  });

  const [sortOrder, setSortOrder] = useState<SortOrder>();
  const [sortedBy, setSortedBy] = useState<keyof ProjectListItemDto>("projectNumber");
  const [projectId, setProjectId] = useState<number>(0);
  const [isProjectOpen, setIsProjectOpen] = useState(false);
  const [erpReferenceProjectIdsFilter, setErpReferenceProjectIdsFilter] = useState<number[] | null>(null);
  const lastErpReferenceLookup = useRef<string | null>(null);

  // States for search-function
  const [searchInput, setSearchInput] = useState<string>("");

  // States for filtering-function
  const [selectedStatuses, setSelectedStatuses] = useState<Set<number>>(
    () => new Set(DEFAULT_PROJECT_STATUSES),
  );

  type SortOrder = "asc" | "desc";

  const isMobile = useMediaQuery({ maxWidth: 768 });

  const sortProjects = (key: keyof ProjectListItemDto) => {
    if (!Array.isArray(projects)) {
      console.error("projects is not an array");
      return;
    }

    const newSortOrder =
      sortedBy === key && sortOrder === "asc" ? "desc" : "asc";
    setSortedBy(key);
    setSortOrder(newSortOrder);
    const sortedProjects = [...projects].sort((a, b) => {
      const aValue = a[key] ?? "";
      const bValue = b[key] ?? "";
      if (aValue < bValue) {
        return newSortOrder === "asc" ? -1 : 1;
      }
      if (aValue > bValue) {
        return newSortOrder === "asc" ? 1 : -1;
      }
      return 0;
    });
    setProjects(sortedProjects);
  };

  const handleStatusChange = (status: number | "all", checked: boolean) => {
    if (status === "all") {
      setSelectedStatuses(new Set(checked ? PROJECT_STATUS_FILTERS.map((item) => item.status) : []));
      return;
    }

    setSelectedStatuses((current) => {
      const next = new Set(current);
      if (checked) {
        next.add(status);
      } else {
        next.delete(status);
      }
      return next;
    });
  };

  const getIndicatorColor = (value: number, isArchived: boolean = false) => {
    if (isArchived) {
      return "#1f2937";
    }

    switch (value) {
      case ProjectStatus.WorkPreparationStarted:
        return "#FFE566";
      case ProjectStatus.WorkPreparationFinished:
        return "green";
      case ProjectStatus.Constructed:
        return "blue";
      case ProjectStatus.Closed:
        return "gray";
      default:
        return "black";
    }
  };

  const getProjectStatus = (projectStatus: number, isArchived: boolean = false) => {
    if (isArchived) {
      return "Arkiverad";
    }

    switch (projectStatus) {
      case ProjectStatus.WorkPreparationStarted:
        return "Påbörjad";
      case ProjectStatus.WorkPreparationFinished:
        return "Klar";
      case ProjectStatus.Constructed:
        return "Uppställd";
      case ProjectStatus.Closed:
        return "Avslutad";
      default:
        return "Okänd status";
    }
  };

  const handleOpenProject = (newProjectId: number) => {
    setProjectId(newProjectId);
    setIsProjectOpen(true);
    navigate(`${props.baseUrl}?projectId=${newProjectId}`);
  };

  const backToList = () => {
    props.refetchProjects();
    setIsProjectOpen(false);
    setProjectId(0);
    navigate(props.baseUrl, { replace: true });
  };

  const handleSearchInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e.target.value);
  };

  const filterProject = (status: number) => {
    return selectedStatuses.has(status);
  };

  const filterProjectsBySearch = (project: ProjectListItemDto) => {
    return (
      (project.projectName ?? "").toLowerCase().includes(searchInput.toLowerCase()) ||
      (project.teamLeader ?? "").toLowerCase().includes(searchInput.toLowerCase()) ||
      String(project.projectNumber ?? "").toLowerCase().includes(searchInput.toLowerCase()) ||
      (project.projectAddress ?? "").toLowerCase().includes(searchInput.toLocaleLowerCase())
    );
  };

  const filterProjectsByIdFallback = (project: ProjectListItemDto) => {
    if (!erpReferenceProjectIdsFilter) {
      return true;
    }

    if (project.id === undefined) {
      return false;
    }

    return erpReferenceProjectIdsFilter.includes(project.id);
  };

  const normalizeProjectIdsFromReference = (value: number | number[] | null | undefined): number[] => {
    if (value === null || value === undefined) {
      return [];
    }

    if (Array.isArray(value)) {
      return value.filter((id): id is number => typeof id === "number" && id > 0);
    }

    if (typeof value === "number" && value > 0) {
      return [value];
    }

    return [];
  };

  const [currentPage, setCurrentPage] = useState(1);
  const [projectsPerPage, setProjectsPerPage] = useState(10);

  const isCopyProject = (project: ProjectListItemDto): boolean => {
    return (project.projectName ?? "").toLowerCase().includes("(kopia)");
  };

  const isArchivedProject = (project: ProjectListItemDto): boolean =>
    isServerArchivedListItem(project);

  const getCreationTimestamp = (project: ProjectListItemDto): number => {
    const timestamp = new Date(project.dateOfCreation ?? "").getTime();
    return Number.isFinite(timestamp) ? timestamp : 0;
  };

  // New function to group projects by project number
  const groupProjectsByProjectNumber = (projects: ProjectListItemDto[]): GroupedProject[] => {
    const groupedMap = projects.reduce((acc, project) => {
      const key = String(project.projectNumber ?? "");
      if (!acc.has(key)) {
        acc.set(key, []);
      }
      acc.get(key)!.push(project);
      return acc;
    }, new Map<string, ProjectListItemDto[]>());

    return Array.from(groupedMap.entries()).map(([projectNumber, groupedProjects]) => ({
      projectNumber,
      projects: [...groupedProjects].sort((a, b) => {
        const aIsCopy = isCopyProject(a);
        const bIsCopy = isCopyProject(b);

        if (aIsCopy !== bIsCopy) {
          return aIsCopy ? 1 : -1;
        }

        const creationDateDiff = getCreationTimestamp(a) - getCreationTimestamp(b);
        if (creationDateDiff !== 0) {
          return creationDateDiff;
        }

        return (a.id ?? 0) - (b.id ?? 0);
      })
    }));
  };

  // Modify the filtering and pagination logic
  const filteredProjects = Array.isArray(projects) ? projects
    .filter((project: ProjectListItemDto) => filterProject(project.projectStatus ?? 0))
    .filter((project: ProjectListItemDto) => filterProjectsBySearch(project))
    .filter((project: ProjectListItemDto) => filterProjectsByIdFallback(project))
    : [];

  const groupedProjects = groupProjectsByProjectNumber(filteredProjects);
  const paginatedGroups = groupedProjects.slice(
    (currentPage - 1) * projectsPerPage,
    currentPage * projectsPerPage
  );

  useEffect(() => {
    const maxPage = Math.ceil(groupedProjects.length / projectsPerPage);
    if (currentPage > maxPage && maxPage > 0) {
      setCurrentPage(1);
    }
  }, [groupedProjects.length, currentPage, projectsPerPage]);

  useEffect(() => {
    setProjects(props.listOfProjectDto || []);
  }, [props.listOfProjectDto]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const projectIdFromUrl = params.get("projectId");
    const erpReferenceFromUrl = params.get("erpReference");

    if (projectIdFromUrl) {
      setErpReferenceProjectIdsFilter(null);
      lastErpReferenceLookup.current = null;
      const id = parseInt(projectIdFromUrl, 10);
      if (!isNaN(id) && id !== 0) {
        setProjectId(id);
        setIsProjectOpen(true);
      } else {
        setIsProjectOpen(false);
        setProjectId(0);
      }
      return;
    }

    if (erpReferenceFromUrl) {
      if (lastErpReferenceLookup.current === erpReferenceFromUrl) {
        return;
      }
      lastErpReferenceLookup.current = erpReferenceFromUrl;
      getApiProjectsGetProjectIdByReference({ erpReference: erpReferenceFromUrl })
        .then((result) => {
          const projectIds = normalizeProjectIdsFromReference(result as number | number[] | null | undefined);

          if (projectIds.length === 0) {
            setErpReferenceProjectIdsFilter(null);
            setIsProjectOpen(false);
            setProjectId(0);
            return;
          }

          if (projectIds.length === 1) {
            setErpReferenceProjectIdsFilter(null);
            navigate(`${props.baseUrl}?projectId=${projectIds[0]}`, { replace: true });
            return;
          }

          const matchedProjects = projects.filter(
            (project) => project.id !== undefined && projectIds.includes(project.id)
          );
          const hasClosedProjectInResult = matchedProjects.some(
            (project) => (project.projectStatus ?? 0) === ProjectStatus.Closed
          );
          const uniqueProjectNumbers = Array.from(
            new Set(
              matchedProjects
                .map((project) => String(project.projectNumber ?? "").trim())
                .filter((projectNumber) => projectNumber !== "")
            )
          );

          setIsProjectOpen(false);
          setProjectId(0);
          setCurrentPage(1);

          // Only auto-enable "Avslutad" when this ERP lookup returns multiple projects
          // and at least one of them is closed.
          if (hasClosedProjectInResult) {
            setSelectedStatuses((current) => new Set(current).add(ProjectStatus.Closed));
          }

          if (uniqueProjectNumbers.length === 1) {
            setErpReferenceProjectIdsFilter(null);
            setSearchInput(uniqueProjectNumbers[0]);
            return;
          }

          // Safety fallback: if project numbers differ or cannot be inferred, filter by IDs directly.
          setErpReferenceProjectIdsFilter(projectIds);
        })
        .catch(() => {
          setErpReferenceProjectIdsFilter(null);
          setIsProjectOpen(false);
          setProjectId(0);
        });
      return;
    } else {
      setErpReferenceProjectIdsFilter(null);
      lastErpReferenceLookup.current = null;
      setIsProjectOpen(false);
      setProjectId(0);
    }
  }, [location.search, navigate, projects, props.baseUrl]);

  const handleArchiveProject = async (projectId: number) => {
    try {
      await archiveProject(projectId);
      props.refetchProjects();
      toastSuccess("Projektet har flyttats till Arkiv");
    } catch (error) {
      showErrorNotification("Kunde inte flytta projektet till Arkiv", error);
    }
  };

  const duplicateProject = async (projectId: number) => {
    copyProjectMutation.mutate({ data: { projectId } });
  };

  const dropdownItems = (projectId: number) => {
    const project = projects.find((item) => item.id === projectId);
    const isArchived = project ? isArchivedProject(project) : false;

    return {
      items: [
        {
          key: '1',
          label: 'Kopiera projekt',
          onClick: () => duplicateProject(projectId)
        },
        ...(!isArchived ? [{
          key: '2',
          label: 'Flytta till arkiv',
          onClick: () => handleArchiveProject(projectId)
        }] : []),
      ]
    };
  };
  return (
    <div>
      {isProjectOpen === false && (
        <Container className="width-full">
          <Row className="flex-column flex-md-row align-items-start align-items-md-center">
            <Col xs={12} md={4} lg={5}>
              <h3 className="activeProjectsHeader my-3 my-md-5">{props.title}</h3>
            </Col>
            <Col xs={12} md={8} lg={7}>
              <div className="d-flex flex-wrap justify-content-between justify-content-md-end gap-2">
                <div className="position-relative flex-grow-1 flex-md-grow-0" style={{ maxWidth: '350px' }}>
                  <SearchOutlined className="position-absolute ms-3 text-muted" style={{ top: '50%', transform: 'translateY(-50%)' }} />
                  <Form.Control
                    type="text"
                    placeholder="Sök"
                    className="ps-5"
                    value={searchInput}
                    onChange={handleSearchInputChange}
                    style={{ minWidth: '250px' }}
                  />
                  {searchInput && (
                    <Button
                      variant="link"
                      className="position-absolute end-0 me-3 text-muted"
                      onClick={() => setSearchInput('')}
                      style={{ top: '50%', transform: 'translateY(-50%)' }}
                    >
                      <CloseOutlined />
                    </Button>
                  )}
                </div>
                <OverlayTrigger
                  rootClose={true}
                  trigger="click"
                  placement="bottom"
                  overlay={
                    <Popover id="popover-filter" className="ms-2">
                      <Popover.Header as="h3">Projektstatus</Popover.Header>
                      <Popover.Body>
                        <Form.Group className="hstack">
                          <Form.Check
                            className="checkbox-styling mb-2 me-2 text-start"
                            id="filter-status-all"
                            type="checkbox"
                            checked={PROJECT_STATUS_FILTERS.every((item) => selectedStatuses.has(item.status))}
                            value="all"
                            onChange={(e) =>
                              handleStatusChange("all", e.currentTarget.checked)
                            }
                          ></Form.Check>
                          <Form.Label htmlFor="filter-status-all">Alla projektstatusar</Form.Label>
                        </Form.Group>
                        {PROJECT_STATUS_FILTERS.map((item) => (
                          <Form.Group className="hstack" key={item.status}>
                            <Form.Check
                              className="mb-2 me-2 text-start"
                              id={item.id}
                              type="checkbox"
                              checked={selectedStatuses.has(item.status)}
                              value={item.status}
                              onChange={(e) => handleStatusChange(item.status, e.currentTarget.checked)}
                            />
                            <Form.Label htmlFor={item.id}>
                              <span
                                className="indicator me-2"
                                style={{ backgroundColor: getIndicatorColor(item.status, item.status === ProjectStatus.Archived) }}
                              />
                              {item.label}
                            </Form.Label>
                          </Form.Group>
                        ))}
                      </Popover.Body>
                    </Popover>
                  }
                >
                  <StbButton className="px-4" variant="secondary">
                    <FilterOutlined aria-hidden="true" />
                    Filter
                  </StbButton>
                </OverlayTrigger>
              </div>
            </Col>
          </Row>

          {!isMobile ? (
            // Desktop view with table
            <Table hover className="projectTable">
              <thead>
                <tr>
                  <th
                    className="tableHeader"
                    onClick={() => sortProjects("projectNumber")}
                  >
                    Projektnummer
                    {sortOrder === "desc" && sortedBy === "projectNumber" && (
                      <ArrowDownOutlined />
                    )}
                    {sortOrder === "asc" && sortedBy === "projectNumber" && (
                      <ArrowUpOutlined />
                    )}
                  </th>
                  <th
                    className="tableHeader"
                    onClick={() => sortProjects("projectName")}
                  >
                    Projektnamn
                    {sortOrder === "desc" && sortedBy === "projectName" && (
                      <ArrowDownOutlined />
                    )}
                    {sortOrder === "asc" && sortedBy === "projectName" && (
                      <ArrowUpOutlined />
                    )}
                  </th>
                  <th
                    className="tableHeader"
                    onClick={() => sortProjects("teamLeader")}
                  >
                    Arbetsledare
                    {sortOrder === "desc" && sortedBy === "teamLeader" && <ArrowDownOutlined />}
                    {sortOrder === "asc" && sortedBy === "teamLeader" && <ArrowUpOutlined />}
                  </th>
                  <th
                    className="tableHeader"
                    onClick={() => sortProjects("dateOfConstructionStart")}
                  >
                    Startdatum
                    {sortOrder === "desc" &&
                      sortedBy === "dateOfConstructionStart" && (
                        <ArrowDownOutlined />
                      )}
                    {sortOrder === "asc" &&
                      sortedBy === "dateOfConstructionStart" && (
                        <ArrowUpOutlined />
                      )}
                  </th>
                  <th
                    className="tableHeader"
                    onClick={() => sortProjects("dateOfConstructionEnd")}
                  >
                    Slutdatum
                    {sortOrder === "desc" &&
                      sortedBy === "dateOfConstructionEnd" && (
                        <ArrowDownOutlined />
                      )}
                    {sortOrder === "asc" &&
                      sortedBy === "dateOfConstructionEnd" && (
                        <ArrowUpOutlined />
                      )}
                  </th>
                  <th
                    className="tableHeader"
                    onClick={() => sortProjects("squareMeters")}
                  >
                    Kvm
                    {sortOrder === "desc" && sortedBy === "squareMeters" && (
                      <ArrowDownOutlined />
                    )}
                    {sortOrder === "asc" && sortedBy === "squareMeters" && (
                      <ArrowUpOutlined />
                    )}
                  </th>
                  <th
                    className="py-3 tableHeader"
                    onClick={() => sortProjects("projectStatus")}
                  >
                    Projektstatus
                    {sortOrder === "desc" && sortedBy === "projectStatus" && (
                      <ArrowDownOutlined />
                    )}
                    {sortOrder === "asc" && sortedBy === "projectStatus" && (
                      <ArrowUpOutlined />
                    )}
                  </th>
                  <th className="py-3 tableHeader"></th>
                </tr>
              </thead>
              <tbody>
                {paginatedGroups.map((group) => {
                  const isExpanded = expandedGroups.has(group.projectNumber);
                  const visibleProjects = isExpanded ? group.projects : group.projects.slice(0, 1);

                  return visibleProjects.filter((project) => project.id !== undefined).map((project, index) => (
                    <tr
                      key={project.id}
                      className={`${index === 0 ? 'group-start' : 'group-child'} project-row-fixed`}
                    >
                      <td className="ms-2 py-3" onClick={() => handleOpenProject(project.id!)}>
                        {index === 0 ? group.projectNumber : ''}
                      </td>
                      <td className="py-3" onClick={() => handleOpenProject(project.id!)}>
                        {project.projectName ?? ""}
                      </td>

                      <td className="py-3" onClick={() => handleOpenProject(project.id!)}>
                        {(project.teamLeader ?? "") === "" ? "Ej tilldelad" : project.teamLeader}
                      </td>
                      <td className="py-3" onClick={() => handleOpenProject(project.id!)}>
                        {project.dateOfConstructionStart ? new Date(project.dateOfConstructionStart).toISOString().substring(0, 10) : ""}
                      </td>
                      <td className="py-3" onClick={() => handleOpenProject(project.id!)}>
                        {project.dateOfConstructionEnd ? new Date(project.dateOfConstructionEnd).toISOString().substring(0, 10) : ""}
                      </td>
                      <td className="py-3" onClick={() => handleOpenProject(project.id!)}>
                        {project.squareMeters ?? ""}
                      </td>
                      <td className="py-3" onClick={() => handleOpenProject(project.id!)}>
                        <span
                          style={{
                            backgroundColor: getIndicatorColor(project.projectStatus ?? 0, isArchivedProject(project)),
                          }}
                          className="indicator"
                        ></span>
                        {getProjectStatus(project.projectStatus ?? 0, isArchivedProject(project))}
                      </td>
                      <td className="py-3 project-action-cell">
                        <div className="project-action-wrapper">
                          <Dropdown menu={dropdownItems(project.id!)} trigger={['click']}>
                            <div className="d-flex align-items-center justify-content-center project-action-menu">
                              <MoreOutlined className="fs-5" style={{ cursor: 'pointer' }} />
                            </div>
                          </Dropdown>
                          {index === 0 && group.projects.length > 1 && (
                            <span
                              role="button"
                              onClick={() => toggleGroup(group.projectNumber)}
                              className="group-toggle-link"
                            >
                              {isExpanded
                                ? 'Dölj  ▲'
                                : `Visa (${group.projects.length - 1}) ▼`}
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ));
                })}
              </tbody>
            </Table>
          ) : (
            // Mobile view with cards
            <div className="d-flex flex-column gap-3 mt-4">
              {paginatedGroups.map((group) => {
                const isExpanded = expandedGroups.has(group.projectNumber);
                const visibleProjects = isExpanded ? group.projects : group.projects.slice(0, 1);

                return (
                  <div key={group.projectNumber} className="card p-3">
                    <div className="d-flex align-items-center justify-content-between text-muted">
                      <span>{group.projectNumber}</span>
                      {group.projects.length > 1 && (
                        <Button variant="link" className="p-0" onClick={() => toggleGroup(group.projectNumber)}>
                          {isExpanded ? "Dölj" : "Visa"}
                        </Button>
                      )}
                    </div>
                    {visibleProjects.filter((project) => project.id !== undefined).map((project, index) => (
                      <div key={project.id} className="card-item">
                        <div className="d-flex justify-content-between align-items-center">
                          <h6 className="mb-0" onClick={() => handleOpenProject(project.id!)}>{project.projectName ?? ""}</h6>
                          <Dropdown menu={dropdownItems(project.id!)} trigger={['click']}>
                            <MoreOutlined className="fs-5" style={{ cursor: 'pointer' }} />
                          </Dropdown>
                        </div>
                        <div className="text-muted small" onClick={() => handleOpenProject(project.id!)}>{project.projectAddress ?? ""}</div>
                        <div className="d-flex justify-content-between mt-2" onClick={() => handleOpenProject(project.id!)}>
                          <div>
                            <div className="small">
                              <strong>Uppförande:</strong> {project.dateOfConstructionStart
                                ? new Date(project.dateOfConstructionStart).toISOString().substring(0, 10)
                                : ""}
                            </div>
                            <div className="small">
                              <strong>Slutande:</strong> {project.dateOfConstructionEnd
                                ? new Date(project.dateOfConstructionEnd).toISOString().substring(0, 10)
                                : ""}
                            </div>
                            <div className="small">
                              {(project.teamLeader ?? "") === ""
                                ? "Ej tilldelad"
                                : project.teamLeader}
                            </div>
                          </div>
                          <div className="d-flex align-items-center">
                            <span
                              style={{
                                backgroundColor: getIndicatorColor(project.projectStatus ?? 0, isArchivedProject(project)),
                              }}
                              className="indicator me-2"
                            ></span>
                            <small>{getProjectStatus(project.projectStatus ?? 0, isArchivedProject(project))}</small>
                          </div>
                        </div>
                        {index < group.projects.length - 1 && <hr className="my-2" />}
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          )}

          <Pagination
            className="mt-3"
            current={currentPage}
            onChange={(page) => setCurrentPage(page)}
            onShowSizeChange={(_current, size) => setProjectsPerPage(size)}
            total={groupedProjects.length}
            pageSize={projectsPerPage}
            showSizeChanger
            pageSizeOptions={['10', '20', '50', '100']}
          />
        </Container>
      )}
      {isProjectOpen === true && (
        <div>
          <StbButton className="mt-4" variant="secondary" onClick={() => backToList()}>
            Tillbaka
          </StbButton>
          <ProjectItemPage ProjectId={projectId} />
        </div>
      )}
    </div>
  );
};

export default ProjectList;

